import { B2BTopNav } from './components/B2BTopNav';
import { B2BIndexHero } from './sections/B2BIndexHero';
import { brandsConfig } from './config/brands.config';
import { resolveB2BLink, resolveB2BLinks } from './config/resolve-links';
import { B2B_BRANDS_PATH, B2B_PROFS_PATH } from './constants';
import { B2B_IMAGES } from './images';
import { useOptOutOfDesktopOnly } from './hooks/useOptOutOfDesktopOnly';

export interface B2BIndexPageProps {
    /**
     * Origin of the main consumer site for the current business host (e.g.
     * `https://archiproamerica.com`). Resolves the `mainSite` nav links and the
     * "See ArchiPro in action" CTA so they navigate off the subdomain.
     */
    mainSiteOrigin: string;
}

/**
 * B2B index landing — served at the business subdomain root (`/`). Built to
 * Figma `751:25156`: a single full-bleed hero with the transparent top nav and
 * two audience cards linking to the `/professionals` and `/brands` B2B pages.
 *
 * Reuses the brands page's nav links + tagline (same brand-level chrome); the
 * primary CTA is overridden to "See ArchiPro in action" pointing at the main
 * consumer site.
 */
export const B2BIndexPage = ({ mainSiteOrigin }: B2BIndexPageProps) => {
    useOptOutOfDesktopOnly();

    const { tagline, secondaryCta } = brandsConfig.topNav;
    const links = resolveB2BLinks(brandsConfig.topNav.links, mainSiteOrigin);
    const resolvedSecondaryCta = secondaryCta
        ? resolveB2BLink(secondaryCta, mainSiteOrigin)
        : undefined;

    return (
        <main className="relative w-full bg-white">
            <B2BIndexHero
                headline="Where architecture and design gets discovered. And decided."
                body="ArchiPro connects the three sides of every building project — the brands behind the products and materials, the professionals who specify them and the homeowners choosing both."
                backgroundSrc={B2B_IMAGES.indexHero}
                logomarkSrc={B2B_IMAGES.heroLogomark}
                topNav={
                    <B2BTopNav
                        tagline={tagline}
                        links={links}
                        hideWordmark
                        secondaryCta={resolvedSecondaryCta}
                        primaryCta={{
                            label: 'See ArchiPro in action',
                            href: mainSiteOrigin,
                        }}
                    />
                }
                cards={[
                    {
                        title: 'For Architects & Designers',
                        body: 'A stage for your work. A direct line to the clients it deserves.',
                        ctaLabel: 'Learn More',
                        ctaHref: B2B_PROFS_PATH,
                    },
                    {
                        title: 'For Brands',
                        body: 'Where your brand meets the people already deciding on it.',
                        ctaLabel: 'Learn More',
                        ctaHref: B2B_BRANDS_PATH,
                    },
                ]}
            />
        </main>
    );
};
